import React from 'react';
import { Menu, MenuItem } from '@progress/kendo-react-layout';
import { Popup } from '@progress/kendo-react-popup';
import { AccentTextUnbound } from './AccentTextUnBound';
import { AccentTextAreaUnbound } from './AccentTextAreaUnbound';
import { accentUtils, t } from '../../services/HelperService';
import { contextMenuPopupProps, useContextMenuPopup } from './useContextMenuPopup';
import { pickTemplateItem } from './TemplateItemPickerDlg';

const safeString = (v) => (v ?? '').toString();
const safeItems = (v) => (Array.isArray(v) ? v : []);

// a group marked `picker: true` is listed inline while it is small; above its threshold (default 10 items) the
// menu offers one "Label..." entry that opens the searchable picker instead of a long flyout
const DEFAULT_PICKER_THRESHOLD = 10;
const usesPicker = (group, items) => !!group?.picker && items.length > (Number(group?.pickerThreshold) > 0 ? Number(group.pickerThreshold) : DEFAULT_PICKER_THRESHOLD);

export const AccentTemplateInputUnbound = ({
  templateCatalog,
  onChange,
  defaultValue,
  disabled,
  insertMode = 'token',
  multiline = false,
  ...inputProps
}) => {
  const text = safeString(defaultValue);

  const hasTemplates = React.useMemo(() => {
    const list = Array.isArray(templateCatalog) ? templateCatalog : [];
    return list.some(g => (g?.items?.length ?? 0) > 0);
  }, [templateCatalog]);

  const { show: showContext, offset, menuRef, openAt, close: closeContext } = useContextMenuPopup();

  const lastTargetRef = React.useRef(null);
  const lastSelRef = React.useRef({ start: null, end: null });
  const currentValueRef = React.useRef(text);

  React.useEffect(() => {
    currentValueRef.current = text;
  }, [text]);

  React.useEffect(() => {
    if (!showContext) return;

    const onMouseDown = (e) => {
      try {
        if (e?.target?.closest?.('.acc-template-menu')) return;
      } catch {
        // ignore
      }
      closeContext();
    };

    const onKeyDown = (e) => {
      if ((e?.key ?? '') === 'Escape') closeContext();
    };

    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [closeContext, showContext]);

  const insertSnippet = React.useCallback((snippet) => {
    const valueToInsert = safeString(snippet);
    if (accentUtils.isEmpty(valueToInsert)) return;

    const cur = safeString(currentValueRef.current);
    const sel = lastSelRef.current ?? {};
    let start = Number(sel.start);
    let end = Number(sel.end);

    if (!Number.isFinite(start) || start < 0) start = cur.length;
    if (!Number.isFinite(end) || end < 0) end = start;
    if (end < start) end = start;

    const next = cur.slice(0, start) + valueToInsert + cur.slice(end);
    currentValueRef.current = next;
    onChange?.(next);

    const el = lastTargetRef.current;
    const pos = start + valueToInsert.length;
    window.setTimeout(() => {
      try {
        if (el && typeof el.value !== 'undefined') {
          el.value = next;
        }
        el?.focus?.();
        el?.setSelectionRange?.(pos, pos);
      } catch {
        // ignore
      }
    }, 0);
  }, [onChange]);

  const insertItem = React.useCallback((snippet, onInsert) => {
    if (accentUtils.isEmpty(snippet)) return;
    insertSnippet(snippet);
    // an item's optional onInsert hook runs after the text landed (hosts use it to
    // declare / attach whatever the inserted reference stands for)
    if (typeof onInsert === 'function') {
      try {
        const res = onInsert({ snippet });
        if (res && typeof res.catch === 'function') res.catch(() => { });
      } catch {
        // the insertion itself already happened; the hook's failure is the host's to surface
      }
    }
  }, [insertSnippet]);

  const snippetOf = React.useCallback((it) => {
    const tok = safeString(it?.token).trim();
    const snip = safeString(it?.snippet).trim();
    return insertMode === 'snippet' ? (snip || tok) : tok;
  }, [insertMode]);

  const onTemplateSelect = React.useCallback((e) => {
    const data = e?.item?.data;
    closeContext();
    if (data?.pickerGroup) {
      // the saved caret survives the dialog: the pick lands where the user right-clicked
      const group = data.pickerGroup;
      pickTemplateItem({ title: safeString(group.label).trim() || safeString(group.group), items: safeItems(group.items) })
        .then((it) => { if (it) insertItem(snippetOf(it), it.onInsert); })
        .catch(() => { });
      return;
    }
    insertItem(safeString(data?.snippet ?? data?.id), data?.onInsert);
  }, [closeContext, insertItem, snippetOf]);

  const renderMenuItems = React.useCallback((items, parentKey) => {
    return safeItems(items).map((it, ii) => {
      const tok = safeString(it?.token).trim();
      const lbl = safeString(it?.label).trim();
      const snip = safeString(it?.snippet).trim();
      const children = safeItems(it?.items);

      if (tok) {
        const text = lbl || tok;
        const snippet =
          insertMode === 'snippet'
            ? (snip || tok)
            : tok;
        return (
          <MenuItem
            key={`${parentKey}_leaf_${ii}_${tok}`}
            text={text}
            data={{ id: tok, snippet, onInsert: typeof it?.onInsert === 'function' ? it.onInsert : undefined }}
          />
        );
      }

      if (children.length > 0) {
        const text = lbl || t('Templates');
        return (
          <MenuItem key={`${parentKey}_grp_${ii}_${text}`} text={text}>
            {renderMenuItems(children, `${parentKey}_${ii}`)}
          </MenuItem>
        );
      }

      return null;
    }).filter(Boolean);
  }, [insertMode]);

  const InputComponent = multiline ? AccentTextAreaUnbound : AccentTextUnbound;

  return (
    <>
      {hasTemplates && (
        <Popup show={showContext} offset={offset} {...contextMenuPopupProps}>
          <div ref={menuRef} style={{ display: 'inline-block' }}>
            <Menu
              className="acc-template-menu"
              vertical
              style={{ display: 'inline-block' }}
              onSelect={onTemplateSelect}
            >
              {(templateCatalog ?? []).map((g, gi) => {
                // a group may carry a translated label beside its (convention-keyed) name
                const groupName = safeString(g?.label).trim() || safeString(g?.group) || t('Templates');
                const items = safeItems(g?.items);
                if (items.length === 0) return null;

                if (usesPicker(g, items)) {
                  return (
                    <MenuItem
                      key={`grp_${gi}_${safeString(g?.group) || groupName}_picker`}
                      text={`${groupName}... (${items.length})`}
                      data={{ pickerGroup: g }}
                    />
                  );
                }

                return (
                  <MenuItem key={`grp_${gi}_${safeString(g?.group) || groupName}`} text={groupName}>
                    {renderMenuItems(items, `grp_${gi}`)}
                  </MenuItem>
                );
              }).filter(Boolean)}
            </Menu>
          </div>
        </Popup>
      )}

      <InputComponent
        {...inputProps}
        disabled={!!disabled}
        defaultValue={text}
        resetWhenDefaultValueChanges={!multiline}
        onChange={(value) => {
          currentValueRef.current = safeString(value);
          onChange?.(value);
        }}
        onContextMenu={(e) => {
          if (!hasTemplates) return;
          if (disabled) return;
          try {
            if (e?.preventDefault) e.preventDefault();
          } catch {
            // ignore
          }

          const tgt = e?.target;
          lastTargetRef.current = tgt;
          lastSelRef.current = {
            start: tgt?.selectionStart,
            end: tgt?.selectionEnd,
          };

          openAt(e);
        }}
      />
    </>
  );
};
